<template>
    <page :busy="busy" :tabs="tabs">
        <grid
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            :defaultColDef="defaultColDef"
            ref="grid"
            @on-total-records="setTotalRecords"
            :pinnedBottomRowData="pinnedBottomRowData"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="onInputChange"
            ></date-range-picker>
            <responsive-dropdown :icon="['fas', 'filter']" :items="filterItems" variant="primary"
                >{{ filter.name }}
            </responsive-dropdown>
            <b-button-group aria-label="Export table data">
                <responsive-button
                    :icon="['fas', 'file-excel']"
                    :href="exportExcelHref"
                    @click="generatingExcelReportSuccessToast"
                    variant="primary"
                >
                </responsive-button>
            </b-button-group>
            <responsive-button :icon="['fas', 'rotate']" @click="refreshGrid()" variant="primary"
                >Refresh</responsive-button
            >
            <responsive-button :icon="['fas', 'plus']" href="/app/guest/new/" variant="primary" v-if="false"
                >Add Credit</responsive-button
            >
        </grid>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import parse from 'date-fns/parse';
    import API from '../../../api';
    import Page from '../../generic/page/page.vue';
    import Grid from '../../generic/grid/grid.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
    import DateRangePicker, {
        LAST_MONTH,
        LAST_WEEK,
        LAST_YEAR,
        nameToRange,
        THIS_MONTH,
        THIS_WEEK,
        THIS_YEAR,
        TODAY,
    } from '../../generic/date-range-picker/date-range-picker.vue';

    const SERVER_DATE_FORMAT = 'yyyy-MM-dd';

    export default {
        name: 'GuestCredit',
        components: {
            Page,
            Grid,
            ResponsiveButton,
            ResponsiveDropdown,
            DateRangePicker,
            actionsCellRenderer: ActionsCellRenderer,
        },
        data() {
            return {
                busy: false,
                tabs: [
                    {
                        title: 'Guest Credit',
                        route: '/app/guest/credit/',
                        active: true,
                    },
                ],
                dateRange: nameToRange(THIS_YEAR),
                predefinedRanges: [TODAY, THIS_WEEK, LAST_WEEK, THIS_MONTH, LAST_MONTH, THIS_YEAR, LAST_YEAR],
                defaultColDef: {
                    minWidth: 80,
                    sortable: true,
                    cellClass: ({ data, node }) => {
                        if (data && data.expired) {
                            return 'text-muted';
                        }
                        if (node.rowPinned) {
                            return 'font-weight-bold';
                        }
                        return '';
                    },
                },
                columnDefs: [
                    {
                        field: 'id',
                        type: 'idColumn',
                        maxWidth: 100,
                        valueGetter: ({ node, data }) => {
                            return node.rowPinned ? this.totalRecords : data.id;
                        },
                    },
                    {
                        field: 'created',
                        headerName: 'Created',
                    },
                    {
                        field: 'expired',
                        headerName: 'Status',
                        cellRenderer: ({ data, value, node }) => {
                            if (node.rowPinned) {
                                return '';
                            }
                            const mutedClass = data.expired ? 'text-muted' : '';
                            const text = value
                                ? `<span class="${mutedClass}">Expired</span>`
                                : `<span class="${mutedClass}">Active</span>`;
                            return text;
                        },
                    },
                    {
                        field: 'first_name',
                        headerName: 'First Name',
                    },
                    {
                        field: 'last_name',
                        headerName: 'Last Name',
                    },
                    {
                        field: 'email',
                        headerName: 'Email',
                        minWidth: 250,
                    },
                    {
                        field: 'meta_res_id',
                        headerName: 'Invoice #',
                        flex: 0,
                        minWidth: 110,
                        width: 110,
                        cellRenderer: ({ data, value, node }) => {
                            if (node.rowPinned) {
                                return '';
                            }
                            const mutedClass = data.expired ? 'text-muted' : '';
                            const text =
                                value != null
                                    ? `<a class="${mutedClass}" href="/app/reservation/edit/invoice/${value}">${value}</a>`
                                    : '-';
                            return text;
                        },
                        cellClass: ({ data }) => {
                            if (data && data.expired) {
                                return 'text-muted';
                            }
                            return '';
                        },
                    },
                    {
                        field: 'code',
                        headerName: 'Code',
                    },
                    {
                        type: 'dateEditableColumn',
                        field: 'expires_on',
                        headerName: 'Expires On',
                        editable: ({ data }) => !data.redeemed,
                        minWidth: 120,
                        valueSetter: ({ oldValue, newValue, node, data }) => {
                            (async () => {
                                const expiresOn = format(
                                    parse(newValue, this.userUnicodeDateFormat, new Date()),
                                    SERVER_DATE_FORMAT
                                );
                                try {
                                    await API.put(`/app/guest/credit/list/${data.id}/`, {
                                        expires_on: expiresOn,
                                    });
                                } catch (error) {
                                    this.errorToast({
                                        text: 'Could not save expiration date.',
                                    });
                                    data.expires_on = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.expires_on = newValue;
                            this.refreshGrid();
                            return true;
                        },
                        cellClass: ({ data }) => {
                            const classList = [];
                            if (data && data.expired) {
                                classList.push('text-muted');
                            } else if (data && !data.redeemed) {
                                classList.push('text-primary');
                                classList.push('lx-editable-cell');
                            }
                            return classList;
                        },
                    },
                    {
                        field: 'redeemed_on',
                        headerName: 'Redeemed Date',
                        minWidth: 190,
                        width: 190,
                    },
                    {
                        field: 'redeemed_meta_res_id',
                        headerName: 'Redeemed On',
                        flex: 0,
                        minWidth: 130,
                        width: 130,
                        cellRenderer: ({ data, value, node }) => {
                            if (node.rowPinned) {
                                return '';
                            }

                            const mutedClass = data.expired ? 'text-muted' : '';
                            const text =
                                value != null
                                    ? `<a class="${mutedClass}" href="/app/reservation/edit/invoice/${value}">${value}</a>`
                                    : '-';
                            return text;
                        },
                        cellClass: ({ data }) => {
                            if (data && data.expired) {
                                return 'text-muted';
                            }
                            return '';
                        },
                    },
                    {
                        field: 'amount',
                        headerName: 'Amount',
                    },
                    {
                        headerName: '',
                        maxWidth: 120,
                        resizable: false,
                        sortable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            refresh: () => {
                                this.refreshGrid();
                            },
                        },
                    },
                ],
                offset: 0,
                limit: 20,
                sort: 'id',
                desc: false,
                search: '',
                filter: {
                    sysname: 'ISSUED',
                    name: 'Issued',
                },
                totalRecords: 0,
            };
        },
        beforeCreate() {
            document.body.className = 'lx-guest-credit';
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;
                    const filter = this.filter.sysname;
                    const from = format(this.dateRange.from, SERVER_DATE_FORMAT);
                    const to = format(this.dateRange.to, SERVER_DATE_FORMAT);
                    return API.get(
                        encodeURI(
                            `/app/guest/credit/list/?offset=${offset}&limit=${limit}&sort=${sort}&desc=${desc}&search=${search}&cfilter=${filter}&from_date=${from}&to_date=${to}`
                        )
                    );
                };
            },
            pinnedBottomRowData() {
                return [
                    {
                        id: this.totalRecords,
                    },
                ];
            },
            exportExcelHref() {
                const filter = this.filter.sysname;
                const from = format(this.dateRange.from, SERVER_DATE_FORMAT);
                const to = format(this.dateRange.to, SERVER_DATE_FORMAT);
                return `/app/guest/credit/list/excel/?offset=${this.offset}&limit=${this.limit}&sort=${this.sort}&desc=${this.desc}&cfilter=${filter}&from_date=${from}&to_date=${to}`;
            },
            filterItems() {
                return [
                    {
                        icon: ['fas', 'filter'],
                        label: 'Issued',
                        click: this.setFilterIssued,
                    },
                    {
                        icon: ['fas', 'filter'],
                        label: 'Redeemed',
                        click: this.setFilterRedeemed,
                    },
                    {
                        icon: ['fas', 'filter'],
                        label: 'Outstanding',
                        click: this.setFilterOutstanding,
                    },
                ];
            },
        },
        methods: {
            refreshGrid() {
                this.$refs.grid.refreshDataSource();
            },
            setFilterIssued() {
                this.filter = {
                    sysname: 'ISSUED',
                    name: 'Issued',
                };
                this.refreshGrid();
            },
            setFilterRedeemed() {
                this.filter = {
                    sysname: 'REDEEMED',
                    name: 'Redeemed',
                };
                this.refreshGrid();
            },
            setFilterOutstanding(filter) {
                this.filter = {
                    sysname: 'OUTSTANDING',
                    name: 'Outstanding',
                };
                this.refreshGrid();
            },
            onInputChange(value) {
                this.dateRange = {
                    from: value.from,
                    to: value.to,
                };
                this.refreshGrid();
            },
            setTotalRecords(value) {
                this.totalRecords = value;
                this.$refs.grid.gridOptions.api.setGridOption('pinnedBottomRowData', this.pinnedBottomRowData);
            },
        },
    };
</script>

<style lang="scss">
    body.lx-guest-credit {
        overflow: hidden;
    }
</style>
